import { useEffect, useState, type ReactNode } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { waitingCount } from "../sync/store";
import { connectionLabel } from "./Sidebar";
import { t } from "../i18n";
import {
  IconAgent,
  IconArchive,
  IconBack,
  IconBell,
  IconDesktop,
  IconGear,
  IconKey,
  IconList,
  IconLock,
  IconPalette,
  IconPeople,
  IconPerson,
  IconPersonAdd,
  IconPulse,
  IconScales,
  IconServer,
  IconShield,
  IconSignOut,
  IconSparkle,
  IconSun,
  IconTask,
  IconWorkflow,
} from "./icons";
import { initials } from "./WorkspaceMenu";
import { Avatar } from "./ui";

// NavPage is a page of Settings or of Administration. Its keywords are what
// is on it, so the switcher finds it by them. One with to stands among the
// workspace's own pages, beside the channels, and is only linked from here.
export type NavPage = { key: string; label: string; icon: ReactNode; keywords?: string[]; to?: string };
type Group = [string, NavPage[]];

// settingsGroups are one's own pages: what others see and how Lantern
// works for you, then the account you sign in with.
export const settingsGroups = (): Group[] => [
  [
    t("You"),
    [
      { key: "profile", label: t("Profile"), icon: <IconPerson size={17} />, keywords: [t("About you"), t("Status")] },
      { key: "preferences", label: t("Preferences"), icon: <IconSun size={17} />, keywords: [t("Language"), t("Theme"), t("Text and spacing")] },
      { key: "notifications", label: t("Notifications"), icon: <IconBell size={17} />, keywords: [t("Quiet hours"), t("Desktop notifications")] },
      { key: "agents", label: t("Agents"), icon: <IconAgent size={17} />, keywords: [t("Agents acting for you"), t("Your agents")] },
      { key: "tokens", label: t("API tokens"), icon: <IconKey size={17} />, keywords: [t("Personal tokens"), t("Your agents' tokens")] },
    ],
  ],
  [
    t("Account & security"),
    [
      { key: "account", label: t("Account"), icon: <IconLock size={17} />, keywords: [t("Password")] },
      { key: "security", label: t("Security"), icon: <IconShield size={17} />, keywords: [t("Two-step verification"), t("Passkeys"), t("Recovery codes")] },
      { key: "sessions", label: t("Sessions"), icon: <IconDesktop size={17} />, keywords: [t("Where you're signed in"), t("Sign out")] },
    ],
  ],
];

// AdminFeatures is what the server has of what some admin pages hold; a
// page shows when it has something in it. Unknown, every page shows.
export type AdminFeatures = { retention?: boolean; traces?: boolean; dlp?: boolean; workflows?: boolean };

// adminGroups are the workspace's administration: the workspace, its
// people, its data, its agents and models, and the server it runs on.
export const adminGroups = (f?: AdminFeatures): Group[] => [
  [
    t("Workspace"),
    [
      { key: "workspace", label: t("General"), icon: <IconGear size={17} />, keywords: [t("Workspace settings"), t("Creating channels")] },
      { key: "branding", label: t("Branding"), icon: <IconPalette size={17} />, keywords: [t("Theme")] },
    ],
  ],
  [
    t("People"),
    [
      { key: "accounts", label: t("Accounts"), icon: <IconPeople size={17} /> },
      { key: "directory-groups", label: t("Directory groups"), icon: <IconPeople size={17} /> },
      { key: "invites", label: t("Invitations"), icon: <IconPersonAdd size={17} /> },
    ],
  ],
  [
    t("Data & security"),
    [
      ...(!f || f.retention || f.traces ? [{ key: "retention", label: t("Retention"), icon: <IconArchive size={17} /> }] : []),
      { key: "security", label: t("Security"), icon: <IconShield size={17} />, keywords: [t("Data loss prevention"), t("Confirming it's you")] },
      { key: "discovery", label: t("eDiscovery"), icon: <IconScales size={17} /> },
    ],
  ],
  [
    t("Agents & AI"),
    [
      { key: "agents", label: t("Agents"), icon: <IconAgent size={17} /> },
      { key: "tokens", label: t("API tokens"), icon: <IconKey size={17} />, keywords: [t("Personal tokens")] },
      { key: "models", label: t("Models"), icon: <IconSparkle size={17} /> },
      { key: "hosted", label: t("Hosted agents"), icon: <IconServer size={17} /> },
      { key: "capabilities", label: t("Capabilities & pools"), icon: <IconTask size={17} />, keywords: [t("Pools"), t("Offers"), t("Task policy")] },
      { key: "fleet", label: t("Fleet"), icon: <IconList size={17} />, to: "/fleet" },
      { key: "workflows", label: t("Workflows"), icon: <IconWorkflow size={17} />, to: "/workflows" },
    ],
  ],
  [t("Server"), [{ key: "system", label: t("System"), icon: <IconPulse size={17} />, keywords: [t("License")] }]],
];

// pageLabel is what a page of Settings or Administration is called, for
// the history list.
export function pageLabel(admin: boolean, key: string): string | undefined {
  return (admin ? adminGroups() : settingsGroups()).flatMap(([, pages]) => pages).find((p) => !p.to && p.key === key)?.label;
}

// The last place among the channels and the workspace's pages, where
// Back to chat returns.
let chatAt = "/";

export function rememberChat(path: string) {
  chatAt = path;
}

export function backToChat() {
  navigate(chatAt);
}

// SettingsSidebar stands in for the channels while one's settings or the
// workspace's administration is open: their pages, a way back to the
// channels that shows what waits there, and a way across to the other.
export function SettingsSidebar({ admin, page, onSignOut }: { admin: boolean; page: string; onSignOut: () => void }) {
  const session = useSession();
  const state = useLantern();
  const me = state.me;
  const isAdmin = me?.role === "admin";
  const workspace = state.workspace || "Lantern";
  const [features, setFeatures] = useState<AdminFeatures>({});
  useEffect(() => {
    if (admin && isAdmin) session.api.workspaceSettings().then(setFeatures, () => {});
  }, [session, admin, isAdmin]);

  let waiting = 0;
  for (const c of state.channels.values()) waiting += waitingCount(c.channel);
  const title = admin ? t("Administration") : t("Settings");
  const groups = admin ? (isAdmin ? adminGroups(features) : []) : settingsGroups();
  const base = admin ? "/admin/" : "/settings/";

  return (
    <nav className={`sidebar settings-sidebar ${admin ? "admin" : ""}`} aria-label={title}>
      <button className="search-link back-to-chat" onClick={backToChat}>
        <IconBack size={17} />
        <span className="nav-label">{t("Back to chat")}</span>
        {waiting > 0 && <span className="badge">{waiting > 99 ? "99+" : waiting}</span>}
      </button>
      <div className="settings-head">
        {admin ? (
          <span className="workspace-tile" aria-hidden>
            {initials(workspace)}
          </span>
        ) : (
          me && <Avatar id={me.id} name={me.display_name || me.handle} size={36} />
        )}
        <span>
          <strong>
            {title} <span className={`dot ${state.connection}`} title={connectionLabel(state.connection)} />
          </strong>
          <span className="settings-whose">{admin ? workspace : me?.display_name || me?.handle}</span>
        </span>
      </div>
      {admin && isAdmin && <p className="settings-note">{t("Changes here apply to everyone in {workspace}, and are audited.", { workspace })}</p>}
      {/* The pages scroll on their own, so none of them runs under the foot. */}
      <div className="settings-pages">
        {groups.map(
          ([heading, pages]) =>
            pages.length > 0 && (
              <section key={heading} className="side-section" aria-label={heading}>
                <h2>{heading}</h2>
                {pages.map((p) => {
                  const here = !p.to && p.key === page;
                  return (
                    <button
                      key={p.key}
                      className={`search-link ${here ? "active" : ""}`}
                      aria-current={here ? "page" : undefined}
                      title={p.to ? t("Opens beside your channels") : undefined}
                      onClick={() => navigate(p.to ?? base + p.key)}
                    >
                      {p.icon}
                      <span className="nav-label">{p.label}</span>
                      {p.to && (
                        <span className="leaves" aria-hidden>
                          ↗
                        </span>
                      )}
                    </button>
                  );
                })}
              </section>
            ),
        )}
      </div>
      <div className="settings-foot">
        {admin ? (
          <button className="search-link" onClick={() => navigate("/settings/preferences")}>
            <IconPerson size={17} />
            <span className="nav-label">{t("Your settings")}</span>
          </button>
        ) : (
          isAdmin && (
            <button className="search-link" onClick={() => navigate("/admin/workspace")}>
              <IconShield size={17} />
              <span className="nav-label">{t("Administration")}</span>
            </button>
          )
        )}
        <button className="search-link" onClick={onSignOut}>
          <IconSignOut size={17} />
          <span className="nav-label">{t("Sign out of {workspace}", { workspace })}</span>
        </button>
      </div>
    </nav>
  );
}
